import { useState, type ReactElement } from "react";
import { Building2, Check, Copy } from "lucide-react";
import { Badge, EmptyState, IconButton, Stack, Text } from "@ux/primitives";
import { DataTable, type DataColumn } from "@ux/compositions";
import { copyToClipboard } from "../common/clipboard";
import type { SageOfficeView } from "./types";

export interface SageOfficesListProps {
    readonly rows: readonly SageOfficeView[];
    readonly selectedId: string | null;
    readonly onSelect: (row: SageOfficeView) => void;
    readonly isLoading: boolean;
    readonly error: string | null;
}

export function SageOfficesList({
    rows,
    selectedId,
    onSelect,
    isLoading,
    error,
}: SageOfficesListProps): ReactElement {
    const columns: readonly DataColumn<SageOfficeView>[] = [
        {
            id: "name",
            header: "Name",
            render: (row) => (
                <Stack gap={1}>
                    <Text role="body" weight="semibold">
                        {row.name}
                    </Text>
                    <Text role="muted">
                        {row.address.city}, {row.address.region}
                    </Text>
                    {/* Office id surfaced here so admins can copy it
                        for bulk-import CSVs without first opening the
                        detail panel. The IconButton stops propagation
                        on its own click, so tapping it doesn't also
                        select the row. */}
                    <IdLine id={row.id} />
                </Stack>
            ),
        },
        {
            id: "status",
            header: "Status",
            width: "110px",
            render: (row) =>
                row.isDeleted ? <Badge tone="danger">Deleted</Badge> : <Badge tone="ok">Active</Badge>,
        },
    ];

    if (isLoading && rows.length === 0) {
        return (
            <EmptyState
                icon={<Building2 size={28} />}
                title="Loading sage offices…"
            />
        );
    }
    if (error !== null) {
        return (
            <EmptyState
                icon={<Building2 size={28} />}
                title="Could not load sage offices"
                description={error}
            />
        );
    }

    return (
        <DataTable<SageOfficeView>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            selectedKey={selectedId}
            onSelect={onSelect}
            isRowMuted={(row) => row.isDeleted}
            emptyContent={
                <EmptyState
                    icon={<Building2 size={28} />}
                    title="No sage offices yet"
                    description="Create your first office from the toolbar."
                />
            }
        />
    );
}

/**
 * Compact id + copy button. The copy click is wrapped in a div whose
 * onClick stops propagation so the row's row-select doesn't also fire
 * — copying an id should never navigate.
 */
function IdLine({ id }: { readonly id: string }): ReactElement {
    const [copied, setCopied] = useState(false);

    async function handleCopy(): Promise<void> {
        const ok = await copyToClipboard(id);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
        >
            <Text role="muted">
                <code style={{ fontSize: "0.85em" }}>{id}</code>
            </Text>
            <IconButton
                tone="neutral"
                label={copied ? "Copied" : "Copy office id"}
                icon={copied ? <Check size={14} /> : <Copy size={14} />}
                onClick={() => {
                    void handleCopy();
                }}
            />
        </div>
    );
}
